import { ReactNode } from "react";
import {
  FieldValues,
  FormProvider,
  SubmitHandler,
  UseFormReturn,
} from "react-hook-form";

export type RootErrorProps = {
  /** Error message from form.formState.errors.root */
  message?: string;
};

export type Props<T extends FieldValues> = {
  /** Form instance from useForm hook */
  form: UseFormReturn<T>;
  /** Form content - typically FormField components */
  children: ReactNode;
  /** Submit handler */
  onSubmit?: SubmitHandler<T>;
  /** Additional CSS classes for form element */
  className?: string;
  /** Additional CSS classes for inner content wrapper */
  innerClassName?: string;
  /** Render function for root errors */
  renderRootError?: (props: RootErrorProps) => ReactNode;
  /** Render function for inner content wrapper */
  renderInner?: (props: { children: ReactNode; isSubmitting: boolean }) => ReactNode;
};

/**
 * Base form wrapper that provides react-hook-form context.
 * Uses render props for customizable error display and inner wrapper.
 *
 * @example
 * <FormBase
 *   form={form}
 *   onSubmit={handleSubmit}
 *   renderRootError={({ message }) => <div>{message}</div>}
 * >
 *   {children}
 * </FormBase>
 */
export function FormBase<T extends FieldValues>({
  form,
  children,
  onSubmit,
  className,
  innerClassName,
  renderRootError,
  renderInner,
}: Props<T>) {
  const { isSubmitting, errors } = form.formState;

  const innerContent = renderInner ? (
    renderInner({ children, isSubmitting })
  ) : (
    <div className={innerClassName}>{children}</div>
  );

  return (
    <FormProvider {...form}>
      <form
        onSubmit={onSubmit ? form.handleSubmit(onSubmit) : undefined}
        className={className}
      >
        {innerContent}
        {errors.root && renderRootError?.({ message: errors.root.message })}
      </form>
    </FormProvider>
  );
}

FormBase.displayName = "FormBase";

export default FormBase;

